import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Layers, Globe, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

// Featured Case Studies
export const FeaturedCaseStudies = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const caseStudies = [
    {
      id: 'carbon-green-credits',
      client: 'Carbon & Green Credits',
      title: 'Smart Afforestation & Carbon Compliance',
      description: 'Automated GIS-powered workflows for optimal afforestation zone identification, real-time carbon sequestration estimation, and bio-asset monitoring across large-scale green credit programs.',
      image: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
      metrics: ['Real-time PDD', 'GIS-powered', 'Bio-asset tracking'],
      tag: 'Climate Tech'
    },
    {
      id: 'fpo-enablement',
      client: 'FPO Enablement',
      title: "Women's Empowerment Survey App",
      description: "End-to-end FPO enablement platform strengthening governance, operational execution, and member-level transparency with an integrated women's inclusion and leadership lens.",
      image: '/rural empowerment.jpg',
      metrics: ['Scalable ops', 'Women-led', 'Market linkages'],
      tag: 'AgriTech'
    },
    {
      id: 'esri-uk-website',
      client: 'ESRI UK',
      title: 'Global Website Upgradation & Management',
      description: 'Enterprise-grade web solution with strategic WordPress-AEM integration, custom modules for tailored business logic, and rapid campaign microsite deployment.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&w=800&q=85',
      metrics: ['WordPress + AEM', 'Custom modules', 'Campaign microsites'],
      tag: 'Enterprise Web'
    },
  ];

  return (
    <section ref={ref} id="work" className="py-32 bg-gradient-to-b from-[#0f0f10] to-[#0a0a0b]">
      <div className="container mx-auto px-6">
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-5xl md:text-6xl font-bold mb-6">Featured Work</h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Delivering transformative solutions at national and enterprise scale
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {caseStudies.map((study, index) => (
            <motion.div
              key={study.id}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="w-full lg:col-span-1"
            >
              <Link to={`/work#${study.id}`} className="block h-full">
                <Card className="group bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-500 overflow-hidden cursor-pointer h-full">
                  <CardContent className="p-0">
                    <div className="relative h-56 overflow-hidden">
                      <motion.img
                        src={study.image}
                        alt={study.title}
                        className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity duration-500"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.6 }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-[#0a0a0b]/50 to-transparent" />
                      <div className="absolute top-4 left-4">
                        <span className="bg-white/10 backdrop-blur-sm border border-white/20 px-3 py-1 rounded-full text-xs text-white">
                          {study.tag}
                        </span>
                      </div>
                    </div>

                    <div className="p-6">
                      <p className="text-sm text-cyan-400 mb-2 font-semibold">{study.client}</p>
                      <h3 className="text-xl font-bold mb-3 text-white">{study.title}</h3>
                      <p className="text-gray-400 text-sm mb-4 leading-relaxed">{study.description}</p>

                      <div className="flex flex-wrap gap-2 mb-4">
                        {study.metrics.map((metric) => (
                          <span key={metric} className="text-xs bg-white/5 px-2 py-1 rounded border border-white/10 text-gray-300">
                            {metric}
                          </span>
                        ))}
                      </div>

                      <span className="inline-flex items-center text-cyan-400 group-hover:text-cyan-300 text-sm font-medium transition-colors">
                        View Case Study
                        <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Platforms Section
export const PlatformsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const platforms = [
    {
      name: 'Maps.Guru',
      description: 'Everything you need for beautiful basemaps. Self-host vector tiles, customize styles with AI, and deploy globally.',
      icon: Globe,
      image: 'https://images.unsplash.com/photo-1524661135-423995f22d0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxtYXAlMjBiYXNlbWFwJTIwc2F0ZWxsaXRlJTIwdGlsZXN8ZW58MHx8fHwxNzcwNzIwMDAwfDA&ixlib=rb-4.1.0&q=85',
      features: ['Natural language styling', 'Global vector tiles', 'Visual style editor', 'Beautiful presets', 'Drop-in integration', 'Version control'],
      link: 'https://maps.guru/',
    },
    {
      name: 'Conv.Earth',
      description: 'Data conversion, spatialized. Analyze, visualize, and convert proprietary formats to open standards directly in your browser.',
      icon: Layers,
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxkYXRhJTIwY29udmVyc2lvbiUyMGdlb3NwYXRpYWwlMjBhbmFseXRpY3N8ZW58MHx8fHwxNzcwNzIwMDAwfDA&ixlib=rb-4.1.0&q=85',
      features: ['AI spatial assistant', 'Topology error detection', 'Area calculations', 'File size optimization', 'Open format conversion', 'Browser-based'],
      link: 'https://conv-earth.pronit.workers.dev/',
    },
  ];

  return (
    <section ref={ref} id="platforms" className="py-32 bg-[#0a0a0b] relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-cyan-500/5" />
      
      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-5xl md:text-6xl font-bold mb-6">Proprietary Platforms</h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Enterprise-grade products powering digital transformation
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {platforms.map((platform, index) => {
            const Icon = platform.icon;
            return (
              <motion.div
                key={platform.name}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <a href={platform.link} target="_blank" rel="noopener noreferrer" className="block h-full">
                  <Card className="group bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-500 overflow-hidden cursor-pointer h-full">
                    <CardContent className="p-0 flex flex-col md:flex-row h-full">
                      <div className="md:w-1/2 relative h-64 md:h-auto overflow-hidden">
                        <motion.img
                          src={platform.image}
                          alt={platform.name}
                          className="w-full h-full object-cover opacity-50 group-hover:opacity-70 transition-opacity duration-500"
                          whileHover={{ scale: 1.1 }}
                          transition={{ duration: 0.6 }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-cyan-500/20" />
                      </div>

                      <div className="md:w-1/2 p-8 flex flex-col justify-center">
                        <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 mb-4 w-fit">
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-3xl font-bold mb-3 text-white">{platform.name}</h3>
                        <p className="text-gray-400 mb-6 leading-relaxed">{platform.description}</p>

                        <div className="space-y-2">
                          {platform.features.map((feature) => (
                            <div key={feature} className="flex items-center space-x-2">
                              <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                              <span className="text-sm text-gray-300">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </a>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default { FeaturedCaseStudies, PlatformsSection };
